<template>
  <div class="quiz-question-builder">
    <draggable
      :list="localQuestions"
      group="questions"
      handle=".question-handle"
      @change="emitUpdate"
    >
      <QuizQuestionCard
        v-for="(question, index) in localQuestions"
        :ref="`card-${index}`"
        :key="getQuestionKey(question, index)"
        :question="question"
        :question-index="index"
        :collapsed="!expandedQuestionKeys.includes(getQuestionKey(question))"
        :show-required-toggle="showRequiredToggle"
        :show-correct-answers="showCorrectAnswers"
        class="mb-4"
        @update:question="handleQuestionUpdate(index, $event)"
        @delete="removeQuestion(index)"
        @expand-toggle="handleExpandToggle(question, index)"
        @deleted-answers="$emit('deleted-answers', $event)"
      />
    </draggable>

    <Button outlined color="brandPrimary" icon-left="mdi-plus" class="mt-2" @click="addQuestion">
      Add question
    </Button>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import Button from "@/components/generic/Button.vue";
import QuizQuestionCard from "./components/QuizQuestionCard.vue";
import { createQuestion } from "./quizQuestionTypes";

let uidCounter = 0;

export default {
  name: "QuizQuestionBuilder",
  components: {
    draggable,
    Button,
    QuizQuestionCard,
  },
  props: {
    questions: {
      type: Array,
      required: true,
    },
    showRequiredToggle: {
      type: Boolean,
      default: false,
    },
    showCorrectAnswers: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:questions", "deleted-questions", "deleted-answers"],
  data() {
    return {
      expandedQuestionKeys: [],
    };
  },
  computed: {
    localQuestions: {
      get() {
        return this.questions;
      },
      set(value) {
        this.$emit("update:questions", value);
      },
    },
  },
  watch: {
    questions: {
      immediate: true,
      handler(questions) {
        if (questions.length > 0 && this.expandedQuestionKeys.length === 0) {
          this.expandedQuestionKeys = questions.map((q) => this.getQuestionKey(q));
        }
      },
    },
  },
  mounted() {
    if (this.questions.length === 0) {
      this.addQuestion();
    }
  },
  methods: {
    getQuestionKey(question) {
      if (question._uid) return question._uid;
      if (question.learningQuizQuestionId != null) return `q-${question.learningQuizQuestionId}`;
      if (!question._uid) {
        question._uid = `gen-${++uidCounter}`;
      }
      return question._uid;
    },
    handleExpandToggle(question) {
      const key = this.getQuestionKey(question);
      const idx = this.expandedQuestionKeys.indexOf(key);
      if (idx >= 0) {
        this.expandedQuestionKeys.splice(idx, 1);
      } else {
        this.expandedQuestionKeys.push(key);
      }
    },
    validate() {
      if (!this.questions.length) return false;
      let allValid = true;
      for (let i = 0; i < this.questions.length; i++) {
        const cardRef = this.$refs[`card-${i}`];
        const card = Array.isArray(cardRef) ? cardRef[0] : cardRef;
        if (card && !card.validate()) allValid = false;
      }
      return allValid;
    },
    emitUpdate() {
      this.$emit("update:questions", [...this.localQuestions]);
    },
    addQuestion() {
      const question = createQuestion({ _uid: `new-${++uidCounter}` });
      this.expandedQuestionKeys.push(this.getQuestionKey(question));
      this.$emit("update:questions", [...this.questions, question]);
    },
    removeQuestion(index) {
      const question = this.questions[index];

      // Retain this event for the backend sync if it has an ID
      if (question.learningQuizQuestionId) {
        this.$emit("deleted-questions", [question.learningQuizQuestionId]);
      }
      const updated = this.questions.filter((_, i) => i !== index);
      this.$emit("update:questions", updated);
    },
    handleQuestionUpdate(index, updatedQuestion) {
      const updated = [...this.questions];
      updated[index] = updatedQuestion;
      this.$emit("update:questions", updated);
    },
  },
};
</script>
